import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import ApplicationManagement from "../ApplicationManagement";
import DataManagement from "../DataManagement";
import PayTrack from "../PayTrack";

const API_BASE = "https://tn.server.goatteachers.com/api";

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function dateInputValue(value) {
  if (!value) return "";
  return value.toString().slice(0, 10);
}

function dateTimeInputValue(value) {
  if (!value) return "";
  return value.toString().replace(" ", "T").slice(0, 16);
}

function Pill({ children, tone = "default" }) {
  const tones = {
    default: "border-slate-200 bg-slate-50 text-slate-700",
    danger: "border-red-200 bg-red-50 text-red-700",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${
        tones[tone] || tones.default
      }`}
    >
      {children}
    </span>
  );
}

function Card({ title, description, right, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-900">{title}</h2>
          {description && (
            <p className="mt-1 text-sm leading-6 text-slate-500">
              {description}
            </p>
          )}
        </div>

        {right && <div className="shrink-0">{right}</div>}
      </div>

      {children}
    </section>
  );
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl px-4 py-2.5 text-sm font-black transition ${
        active
          ? "bg-slate-950 text-white shadow-sm"
          : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function ActionButton({ children, onClick, variant = "default", disabled }) {
  const variants = {
    default:
      "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
    dark: "border border-slate-950 bg-slate-950 text-white hover:bg-slate-800",
    danger: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl px-4 py-2.5 text-sm font-black shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
        variants[variant] || variants.default
      }`}
    >
      {children}
    </button>
  );
}

function DashboardMetricCard({ label, value, hint, tone = "default" }) {
  const toneClasses = {
    default: {
      card: "border-slate-200 bg-white",
      icon: "bg-slate-100 text-slate-700",
      value: "text-slate-950",
    },
    warning: {
      card: "border-amber-200 bg-amber-50",
      icon: "bg-amber-100 text-amber-700",
      value: "text-amber-700",
    },
    danger: {
      card: "border-red-200 bg-red-50",
      icon: "bg-red-100 text-red-700",
      value: "text-red-700",
    },
    success: {
      card: "border-emerald-200 bg-emerald-50",
      icon: "bg-emerald-100 text-emerald-700",
      value: "text-emerald-700",
    },
    blue: {
      card: "border-blue-200 bg-blue-50",
      icon: "bg-blue-100 text-blue-700",
      value: "text-blue-700",
    },
  };

  const current = toneClasses[tone] || toneClasses.default;

  return (
    <div
      className={`rounded-2xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${current.card}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-black text-slate-600">{label}</p>
          <p
            className={`mt-4 text-4xl font-black tracking-tight ${current.value}`}
          >
            {value}
          </p>
        </div>

        <div
          className={`flex h-10 w-10 items-center justify-center rounded-xl text-xs font-black ${current.icon}`}
        >
          TN
        </div>
      </div>

      <p className="mt-4 min-h-10 text-sm leading-5 text-slate-500">{hint}</p>
    </div>
  );
}

function EmptyState({ message = "No items found." }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm font-semibold text-slate-500">
      {message}
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
      {message}
    </div>
  );
}

function LoadingState({ message = "Loading..." }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm font-bold text-slate-500">
      {message}
    </div>
  );
}

function ListRow({ children }) {
  return (
    <div className="border-b border-slate-100 py-4 last:border-b-0">
      {children}
    </div>
  );
}

const TUITION_ADMIN_STATUSES = [
  "Active",
  "Inactive",
  "Guardian Contacted",
  "Tutor Shortlisted",
  "Trial Scheduled",
  "Confirmed",
  "Cancelled",
  "Fake / Invalid",
];

const GUARDIAN_RESPONSE_STATUSES = [
  { value: "not_contacted", label: "Not contacted" },
  { value: "contacted", label: "Contacted" },
  { value: "interested", label: "Interested" },
  { value: "not_responding", label: "Not responding" },
];

function tuitionStatusTone(status) {
  if (status === "Active") return "success";
  if (status === "Confirmed") return "success";
  if (status === "Cancelled") return "danger";
  if (status === "Fake / Invalid") return "danger";
  if (status === "Inactive") return "default";
  return "warning";
}

const TUTOR_ADMIN_STATUSES = ["Active", "Suspended"];

function tutorStatusTone(status) {
  if (status === "Active") return "success";
  if (status === "Suspended") return "danger";
  return "default";
}

export default function AdminDashboard() {
  const { user, logoutUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [tab, setTab] = useState("dashboard");

  // ---- Dashboard state ----
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");

  // ---- Tuition Management state ----
  const [adminTuitions, setAdminTuitions] = useState([]);
  const [adminTuitionsTotal, setAdminTuitionsTotal] = useState(0);
  const [adminTuitionsLoading, setAdminTuitionsLoading] = useState(false);
  const [adminTuitionsError, setAdminTuitionsError] = useState("");

  const [tuitionSearchInput, setTuitionSearchInput] = useState("");
  const [tuitionSearch, setTuitionSearch] = useState("");
  const [tuitionStatus, setTuitionStatus] = useState("all");

  const [expandedTuitionId, setExpandedTuitionId] = useState(null);
  const [appliedTutorsByTuition, setAppliedTutorsByTuition] = useState({});
  const [appliedTutorsLoadingId, setAppliedTutorsLoadingId] = useState(null);

  const [tuitionDrafts, setTuitionDrafts] = useState({});
  const [tuitionSavingId, setTuitionSavingId] = useState(null);

  const [copiedTuitionId, setCopiedTuitionId] = useState(null);

  // ---- Tutor Management state ----
  const [adminTutors, setAdminTutors] = useState([]);
  const [adminTutorsTotal, setAdminTutorsTotal] = useState(0);
  const [adminTutorsSummary, setAdminTutorsSummary] = useState({
    totalTutors: 0,
    activeTutors: 0,
    suspendedTutors: 0,
    totalCommissionDue: 0,
  });
  const [adminTutorsLoading, setAdminTutorsLoading] = useState(false);
  const [adminTutorsError, setAdminTutorsError] = useState("");

  const [tutorSearchInput, setTutorSearchInput] = useState("");
  const [tutorSearch, setTutorSearch] = useState("");
  const [tutorStatus, setTutorStatus] = useState("all");

  const [tutorDrafts, setTutorDrafts] = useState({});
  const [tutorSavingId, setTutorSavingId] = useState(null);

  // ---- Topups state ----
  const [topupStatus, setTopupStatus] = useState("pending");
  const [topups, setTopups] = useState([]);
  const [topupsTotal, setTopupsTotal] = useState(0);
  const [topupsLoading, setTopupsLoading] = useState(true);
  const [topupsError, setTopupsError] = useState("");

  // ---- Tutor profile reveal state ----
  const [tutorReveals, setTutorReveals] = useState([]);
  const [tutorRevealsTotal, setTutorRevealsTotal] = useState(0);
  const [tutorLoading, setTutorLoading] = useState(true);
  const [tutorError, setTutorError] = useState("");

  // ---- Pagination ----
  const [limit] = useState(30);
  const [offset, setOffset] = useState(0);

  // ---- Reject modal ----
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const handleLogout = () => {
    logoutUser?.();
    navigate("/admin-login");
  };

  useEffect(() => {
    if (!user?.token) {
      navigate("/admin-login");
      return;
    }

    if (!["admin", "support"].includes(user.role)) {
      navigate("/login");
    }
  }, [user, navigate]);

  const authHeaders = useMemo(() => {
    return user?.token ? { Authorization: `Bearer ${user.token}` } : {};
  }, [user]);

  const fetchDashboard = async () => {
    try {
      setDashboardLoading(true);
      setDashboardError("");

      const [dashboardRes, tutorsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/dashboard`, {
          headers: authHeaders,
        }),

        axios.get(`${API_BASE}/admin/tutors`, {
          headers: authHeaders,
          params: {
            search: "",
            status: "all",
            limit: 1,
            offset: 0,
          },
        }),
      ]);

      setDashboard({
        ...(dashboardRes.data?.data || {}),
        activeTutors: tutorsRes.data?.summary?.activeTutors || 0,
        totalTutors: tutorsRes.data?.summary?.totalTutors || 0,
      });
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setDashboardError(
        err?.response?.data?.message || "Failed to load dashboard",
      );

      if ([401, 403].includes(err?.response?.status)) {
        logoutUser?.();
        navigate("/admin-login");
      }
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchTopups = async () => {
    try {
      setTopupsLoading(true);
      setTopupsError("");

      const res = await axios.get(`${API_BASE}/admin/topups`, {
        headers: authHeaders,
        params: { status: topupStatus, limit, offset },
      });

      setTopups(res.data?.data || []);
      setTopupsTotal(Number(res.data?.total || 0));
    } catch (err) {
      console.error("Topups fetch error:", err);
      setTopupsError(err?.response?.data?.message || "Failed to load topups");

      if ([401, 403].includes(err?.response?.status)) {
        logoutUser?.();
        navigate("/admin-login");
      }
    } finally {
      setTopupsLoading(false);
    }
  };

  const fetchTutorReveals = async () => {
    try {
      setTutorLoading(true);
      setTutorError("");

      const res = await axios.get(`${API_BASE}/admin/reveals/tutor-profiles`, {
        headers: authHeaders,
        params: { limit, offset },
      });

      setTutorReveals(res.data?.data || []);
      setTutorRevealsTotal(Number(res.data?.total || 0));
    } catch (err) {
      console.error("Tutor reveals fetch error:", err);
      setTutorError(
        err?.response?.data?.message || "Failed to load tutor reveals",
      );

      if ([401, 403].includes(err?.response?.status)) {
        logoutUser?.();
        navigate("/admin-login");
      }
    } finally {
      setTutorLoading(false);
    }
  };

  const fetchAdminTuitions = async () => {
    try {
      setAdminTuitionsLoading(true);
      setAdminTuitionsError("");

      const res = await axios.get(`${API_BASE}/admin/tuitions`, {
        headers: authHeaders,
        params: {
          search: tuitionSearch,
          status: tuitionStatus,
          limit,
          offset,
        },
      });

      setAdminTuitions(res.data?.data || []);
      setAdminTuitionsTotal(Number(res.data?.total || 0));
    } catch (err) {
      console.error("Admin tuitions fetch error:", err);
      setAdminTuitionsError(
        err?.response?.data?.message || "Failed to load tuitions",
      );

      if ([401, 403].includes(err?.response?.status)) {
        logoutUser?.();
        navigate("/admin-login");
      }
    } finally {
      setAdminTuitionsLoading(false);
    }
  };

  const fetchAdminTutors = async () => {
    try {
      setAdminTutorsLoading(true);
      setAdminTutorsError("");

      const res = await axios.get(`${API_BASE}/admin/tutors`, {
        headers: authHeaders,
        params: {
          search: tutorSearch,
          status: tutorStatus,
          limit,
          offset,
        },
      });

      setAdminTutors(res.data?.data || []);
      setAdminTutorsTotal(Number(res.data?.total || 0));
      setAdminTutorsSummary(
        res.data?.summary || {
          totalTutors: 0,
          activeTutors: 0,
          suspendedTutors: 0,
          totalCommissionDue: 0,
        },
      );
    } catch (err) {
      console.error("Admin tutors fetch error:", err);
      setAdminTutorsError(
        err?.response?.data?.message || "Failed to load tutors",
      );

      if ([401, 403].includes(err?.response?.status)) {
        logoutUser?.();
        navigate("/admin-login");
      }
    } finally {
      setAdminTutorsLoading(false);
    }
  };

  const fetchAppliedTutors = async (tuitionId) => {
    try {
      setAppliedTutorsLoadingId(tuitionId);

      const res = await axios.get(
        `${API_BASE}/admin/tuitions/${tuitionId}/applied-tutors`,
        { headers: authHeaders },
      );

      setAppliedTutorsByTuition((prev) => ({
        ...prev,
        [tuitionId]: res.data?.data || [],
      }));
    } catch (err) {
      console.error("Applied tutors fetch error:", err);
      alert(err?.response?.data?.message || "Failed to load applied tutors");
    } finally {
      setAppliedTutorsLoadingId(null);
    }
  };

  const toggleAppliedTutors = async (tuitionId) => {
    if (expandedTuitionId === tuitionId) {
      setExpandedTuitionId(null);
      return;
    }

    setExpandedTuitionId(tuitionId);

    if (!appliedTutorsByTuition[tuitionId]) {
      await fetchAppliedTutors(tuitionId);
    }
  };

  const updateTuitionDraft = (tuitionId, patch) => {
    setTuitionDrafts((prev) => ({
      ...prev,
      [tuitionId]: {
        ...(prev[tuitionId] || {}),
        ...patch,
      },
    }));
  };

  const saveTuition = async (tuition) => {
    const tuitionId = tuition.tuitionId;
    const draft = tuitionDrafts[tuitionId] || {};

    const body = {
      admin_status: draft.admin_status ?? tuition.adminStatus,
      guardian_response_status:
        draft.guardian_response_status ?? tuition.guardianResponseStatus,
      admin_note: draft.admin_note ?? tuition.adminNote ?? "",
      last_contacted_at:
        draft.last_contacted_at ?? dateTimeInputValue(tuition.lastContactedAt),
      next_followup_date:
        draft.next_followup_date ?? dateInputValue(tuition.nextFollowupDate),
    };

    try {
      setTuitionSavingId(tuitionId);

      await axios.patch(`${API_BASE}/admin/tuitions/${tuitionId}`, body, {
        headers: authHeaders,
      });

      setTuitionDrafts((prev) => {
        const copy = { ...prev };
        delete copy[tuitionId];
        return copy;
      });

      await fetchAdminTuitions();
      alert("Tuition updated");
    } catch (err) {
      console.error("Save tuition error:", err);
      alert(err?.response?.data?.message || "Failed to update tuition");
    } finally {
      setTuitionSavingId(null);
    }
  };

  const handleTuitionSearch = () => {
    setOffset(0);
    setTuitionSearch(tuitionSearchInput.trim());
  };

  const handleTutorSearch = () => {
    setOffset(0);
    setTutorSearch(tutorSearchInput.trim());
  };

  const updateTutorDraft = (tutorId, patch) => {
    setTutorDrafts((prev) => ({
      ...prev,
      [tutorId]: {
        ...(prev[tutorId] || {}),
        ...patch,
      },
    }));
  };

  const saveTutor = async (tutor) => {
    const tutorId = tutor.tutorId;
    const draft = tutorDrafts[tutorId] || {};

    const body = {
      admin_status: draft.admin_status ?? tutor.adminStatus ?? "Active",

      admin_note: draft.admin_note ?? tutor.adminNote ?? "",
    };

    try {
      setTutorSavingId(tutorId);

      await axios.patch(`${API_BASE}/admin/tutors/${tutorId}`, body, {
        headers: authHeaders,
      });

      await axios.patch(
        `${API_BASE}/admin/tutors/${tutorId}/status`,
        {
          status: body.admin_status,
        },
        {
          headers: authHeaders,
        },
      );

      setTutorDrafts((prev) => {
        const copy = { ...prev };
        delete copy[tutorId];
        return copy;
      });

      await fetchAdminTutors();
      alert("Tutor updated");
    } catch (err) {
      console.error("Save tutor error:", err);
      alert(err?.response?.data?.message || "Failed to update tutor");
    } finally {
      setTutorSavingId(null);
    }
  };

  useEffect(() => {
    if (!user?.token || !["admin", "support"].includes(user.role)) return;

    if (tab === "dashboard") fetchDashboard();
    if (tab === "tuitions") fetchAdminTuitions();
    if (tab === "tutors") fetchAdminTutors();
    if (tab === "topups") fetchTopups();
    if (tab === "tutorReveals") fetchTutorReveals();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    topupStatus,
    tuitionStatus,
    tuitionSearch,
    tutorStatus,
    tutorSearch,
    offset,
    user,
    authHeaders,
  ]);

  const approve = async (id) => {
    if (!window.confirm("Approve this topup and add credits?")) return;

    try {
      await axios.post(
        `${API_BASE}/admin/topups/${id}/approve`,
        {},
        { headers: authHeaders },
      );

      await fetchTopups();
      alert("Approved");
    } catch (err) {
      console.error("Approve error:", err);
      alert(err?.response?.data?.message || "Approve failed");
    }
  };

  const openReject = (id) => {
    setRejectId(id);
    setRejectReason("");
    setRejectOpen(true);
  };

  const reject = async () => {
    if (!rejectId) return;

    try {
      await axios.post(
        `${API_BASE}/admin/topups/${rejectId}/reject`,
        { reason: rejectReason?.trim() || null },
        { headers: authHeaders },
      );

      setRejectOpen(false);
      setRejectId(null);
      setRejectReason("");

      await fetchTopups();
      alert("Rejected");
    } catch (err) {
      console.error("Reject error:", err);
      alert(err?.response?.data?.message || "Reject failed");
    }
  };

  const navItems = [
    { key: "dashboard", label: "Dashboard" },
    { key: "tuitions", label: "Tuition Management" },
    { key: "tutors", label: "Tutor Management" },
    { key: "applications", label: "Applications" },

    ...(user?.role === "admin"
      ? [
          { key: "payTrack", label: "PayTrack" },
          { key: "dataManagement", label: "Data Management" },
        ]
      : []),

    { key: "topups", label: "Topups" },
    { key: "tutorReveals", label: "Student Tracker" },
  ];

  const dashboardCards = [
    {
      label: "Active Tuition Posts",
      value: dashboard?.activeTuitionPosts || 0,
      hint: "Currently live posts visible to tutors.",
      tone: "success",
    },
    {
      label: "Total Tutors",
      value: dashboard?.activeTutors || 0,
      hint: "Tutors currently active and allowed to apply.",
      tone: "success",
    },
    {
      label: "Posts Near 3 Applications",
      value: dashboard?.postsNearThreeApplications || 0,
      hint: "Posts that are close to receiving enough tutor applications.",
      tone: "blue",
    },
    {
      label: "New Applications Today",
      value: dashboard?.newApplicationsToday || 0,
      hint: "Tutors who applied today.",
      tone: "default",
    },
    {
      label: "Guardian Follow-up Needed",
      value: dashboard?.guardianFollowupNeeded || 0,
      hint: "Tutor connected, admin should contact guardian.",
      tone: "warning",
    },
    {
      label: "Confirmation Pending",
      value: dashboard?.confirmationPending || 0,
      hint: "Tutor claims tuition is confirmed; admin verification needed.",
      tone: "warning",
    },
    {
      label: "Payment Due",
      value: dashboard?.paymentDue || 0,
      hint: "50% commission collection is pending.",
      tone: "blue",
    },
    // {
    //   label: "Payment Overdue",
    //   value: dashboard?.paymentOverdue || 0,
    //   hint: "More than 7 days after confirmation and still unpaid.",
    //   tone: "danger",
    // },
  ];

  const totalForTab =
    tab === "tuitions"
      ? adminTuitionsTotal
      : tab === "tutors"
        ? adminTutorsTotal
        : tab === "topups"
          ? topupsTotal
          : tab === "tutorReveals"
            ? tutorRevealsTotal
            : 0;

  const canPrev = offset > 0;
  const canNext = offset + limit < totalForTab;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-slate-400">
              Tutor Network Admin
            </p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
              Admin Dashboard
            </h1>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
              Monitor tuition posts, tutor applications, contact reveals,
              confirmations, and commission collection.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="success">Admin</Pill>
            <ActionButton onClick={handleLogout}>Logout</ActionButton>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {navItems.map((item) => (
            <TabButton
              key={item.key}
              active={tab === item.key}
              onClick={() => {
                setTab(item.key);
                setOffset(0);
              }}
            >
              {item.label}
            </TabButton>
          ))}
        </div>

        <div className="space-y-6">
          {/* Main Dashboard */}
          {tab === "dashboard" && (
            <>
              <Card
                title="Main Dashboard"
                description="A quick operational overview of Tutor Network activity."
                right={
                  <ActionButton onClick={fetchDashboard}>Refresh</ActionButton>
                }
              >
                {dashboardLoading ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div
                        key={index}
                        className="h-40 animate-pulse rounded-2xl border border-slate-200 bg-slate-100"
                      />
                    ))}
                  </div>
                ) : dashboardError ? (
                  <ErrorState message={dashboardError} />
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {dashboardCards.map((item) => (
                      <DashboardMetricCard
                        key={item.label}
                        label={item.label}
                        value={item.value}
                        hint={item.hint}
                        tone={item.tone}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {/* Tuition Management */}
          {tab === "tuitions" && (
            <Card
              title="Tuition Management"
              description="Manage tuition posts, guardian follow-ups, tutor connections, and admin workflow status."
              right={
                <div className="flex flex-wrap items-center gap-2">
                  <Pill>Total: {adminTuitionsTotal}</Pill>

                  <select
                    value={tuitionStatus}
                    onChange={(e) => setTuitionStatus(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All statuses</option>
                    {TUITION_ADMIN_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>

                  <ActionButton onClick={fetchAdminTuitions}>
                    Refresh
                  </ActionButton>
                </div>
              }
            >
              <div className="mb-5 flex flex-col gap-3 lg:flex-row">
                <input
                  value={tuitionSearchInput}
                  onChange={(e) => setTuitionSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTuitionSearch();
                  }}
                  placeholder="Search by ID, name, phone, subject, medium, location..."
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400"
                />

                <div className="flex gap-2">
                  <ActionButton variant="dark" onClick={handleTuitionSearch}>
                    Search
                  </ActionButton>

                  <ActionButton
                    onClick={() => {
                      setTuitionSearchInput("");
                      setTuitionSearch("");
                      setOffset(0);
                    }}
                  >
                    Clear
                  </ActionButton>
                </div>
              </div>

              {adminTuitionsLoading ? (
                <LoadingState message="Loading tuition posts..." />
              ) : adminTuitionsError ? (
                <ErrorState message={adminTuitionsError} />
              ) : adminTuitions.length === 0 ? (
                <EmptyState message="No tuition posts found." />
              ) : (
                <div className="space-y-4">
                  {adminTuitions.map((t) => {
                    const draft = tuitionDrafts[t.tuitionId] || {};

                    const currentAdminStatus =
                      draft.admin_status ?? t.adminStatus ?? "Active";

                    const currentGuardianStatus =
                      draft.guardian_response_status ??
                      t.guardianResponseStatus ??
                      "not_contacted";

                    const currentNote = draft.admin_note ?? t.adminNote ?? "";

                    const currentLastContacted =
                      draft.last_contacted_at ??
                      dateTimeInputValue(t.lastContactedAt);

                    const currentNextFollowup =
                      draft.next_followup_date ??
                      dateInputValue(t.nextFollowupDate);

                    const appliedTutors =
                      appliedTutorsByTuition[t.tuitionId] || [];

                    const cleanText = (value, fallback = "—") => {
                      if (Array.isArray(value))
                        return value.filter(Boolean).join(", ") || fallback;
                      return value || fallback;
                    };

                    const getTuitionDays = (t) => {
                      return cleanText(
                        t.days ||
                          t.weekdays ||
                          t.weekDays ||
                          t.tuitionDays ||
                          t.tuition_days ||
                          t.classDays ||
                          t.class_days ||
                          t.daysPerWeek ||
                          t.days_per_week ||
                          t.numberOfDays ||
                          t.number_of_days ||
                          t.weeklyDays ||
                          t.weekly_days,
                        "—",
                      );
                    };

                    const getPreferredBackground = (t) => {
                      return cleanText(
                        t.preferredBackground ||
                          t.preferred_background ||
                          t.institutePreference ||
                          t.institutePreferences ||
                          t.preferredInstitute ||
                          t.preferred_institute,
                        "Any suitable university background",
                      );
                    };

                    const getTuitionLocation = (t) => {
                      return (
                        [t.areas, t.districtName].filter(Boolean).join(", ") ||
                        "—"
                      );
                    };

                    const getTuitionSalary = (t) => {
                      if (t.minSalary || t.maxSalary) {
                        return ` ${t.maxSalary || 0} BDT`;
                      }

                      return "—";
                    };

                    const buildTuitionPostText = (t) => {
                      const tuitionId = t.tuitionId || t.id || "—";

                      return `

🟢 ${cleanText(t.genderPreference)} Tutor - ${getTuitionLocation(t)}
📌 Tuition Code: #TN${tuitionId}
🎓 Student Level: ${cleanText(t.levelName)}
📚 Medium: ${cleanText(t.mediumName)}
📖 Subject(s): ${cleanText(t.subjects)}
🗓️ Days: ${getTuitionDays(t)}
💰 Salary: ${getTuitionSalary(t)}
👨‍🏫 Preferred Gender: ${cleanText(t.genderPreference)}
📍 INBOX TO APPLY: ${tuitionId}`;
                    };

                    const copyTuitionPost = async (t) => {
                      const tuitionId = String(t.tuitionId || t.id);
                      const postText = buildTuitionPostText(t);

                      try {
                        if (navigator.clipboard && window.isSecureContext) {
                          await navigator.clipboard.writeText(postText);
                        } else {
                          const textarea = document.createElement("textarea");
                          textarea.value = postText;
                          textarea.style.position = "fixed";
                          textarea.style.left = "-9999px";
                          document.body.appendChild(textarea);
                          textarea.focus();
                          textarea.select();
                          document.execCommand("copy");
                          document.body.removeChild(textarea);
                        }

                        setCopiedTuitionId(tuitionId);

                        setTimeout(() => {
                          setCopiedTuitionId((currentId) =>
                            currentId === tuitionId ? null : currentId,
                          );
                        }, 1200);
                      } catch (error) {
                        console.error("Failed to copy tuition post:", error);
                      }
                    };

                    return (
                      <div
                        key={t.tuitionId}
                        className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                      >
                        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-base font-black text-slate-950">
                                {t.studentName || "—"}{" "}
                                <span className="font-semibold text-slate-400">
                                  ({t.posterType || "—"})
                                </span>
                              </h3>

                              <Link
                                to={`/tuition/${t.tuitionId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Pill tone="blue">
                                  Tuition ID: {t.tuitionId}
                                </Pill>
                              </Link>

                              <Pill
                                tone={tuitionStatusTone(currentAdminStatus)}
                              >
                                {currentAdminStatus}
                              </Pill>

                              <Pill tone="blue">
                                {Number(t.applicationCount || 0)} Applied
                              </Pill>

                              {Number(t.applicationCount || 0) >= 3 && (
                                <Pill tone="warning">Near/Reached 3</Pill>
                              )}
                            </div>

                            <p className="mt-2 text-sm font-bold text-slate-700"></p>

                            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-3">
                              <div>
                                <span className="font-black text-slate-800">
                                  Phone:
                                </span>{" "}
                                {t.phoneNumber || "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  WhatsApp:
                                </span>{" "}
                                {t.whatsappNumber || "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  Gender:
                                </span>{" "}
                                {t.genderPreference || "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  Class:
                                </span>{" "}
                                {t.levelName || "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  Medium:
                                </span>{" "}
                                {t.mediumName || "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  Subjects:
                                </span>{" "}
                                {t.subjects || "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  Location:
                                </span>{" "}
                                {[t.divisionName, t.districtName, t.areas]
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  Salary:
                                </span>{" "}
                                {t.minSalary || t.maxSalary
                                  ? ` ${t.maxSalary || 0} BDT`
                                  : "—"}
                              </div>
                              <div>
                                <span className="font-black text-slate-800">
                                  Posted:
                                </span>{" "}
                                {formatDate(t.postedAt)}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <ActionButton
                              onClick={() => toggleAppliedTutors(t.tuitionId)}
                            >
                              {expandedTuitionId === t.tuitionId
                                ? "Hide Tutors"
                                : "View Tutors"}
                            </ActionButton>

                            <ActionButton onClick={() => copyTuitionPost(t)}>
                              {copiedTuitionId === String(t.tuitionId || t.id)
                                ? "✓ Copied"
                                : "Copy Tuition Post"}
                            </ActionButton>

                            <ActionButton
                              variant="dark"
                              disabled={tuitionSavingId === t.tuitionId}
                              onClick={() => saveTuition(t)}
                            >
                              {tuitionSavingId === t.tuitionId
                                ? "Saving..."
                                : "Save"}
                            </ActionButton>
                          </div>
                        </div>

                        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
                          <div>
                            <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                              Admin Status
                            </label>
                            <select
                              value={currentAdminStatus}
                              onChange={(e) =>
                                updateTuitionDraft(t.tuitionId, {
                                  admin_status: e.target.value,
                                })
                              }
                              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:border-slate-400"
                            >
                              {TUITION_ADMIN_STATUSES.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* <div>
                            <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                              Guardian Response
                            </label>
                            <select
                              value={currentGuardianStatus}
                              onChange={(e) =>
                                updateTuitionDraft(t.tuitionId, {
                                  guardian_response_status: e.target.value,
                                })
                              }
                              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:border-slate-400"
                            >
                              {GUARDIAN_RESPONSE_STATUSES.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                              Last Contacted At
                            </label>
                            <input
                              type="datetime-local"
                              value={currentLastContacted}
                              onChange={(e) =>
                                updateTuitionDraft(t.tuitionId, {
                                  last_contacted_at: e.target.value,
                                })
                              }
                              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:border-slate-400"
                            />
                          </div>

                          <div>
                            <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                              Next Follow-up Date
                            </label>
                            <input
                              type="date"
                              value={currentNextFollowup}
                              onChange={(e) =>
                                updateTuitionDraft(t.tuitionId, {
                                  next_followup_date: e.target.value,
                                })
                              }
                              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:border-slate-400"
                            />
                          </div> */}
                        </div>

                        <div className="mt-4">
                          <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                            Admin Note
                          </label>
                          <textarea
                            value={currentNote}
                            onChange={(e) =>
                              updateTuitionDraft(t.tuitionId, {
                                admin_note: e.target.value,
                              })
                            }
                            placeholder="Internal note for this tuition..."
                            rows={3}
                            className="mt-2 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400"
                          />
                        </div>

                        {expandedTuitionId === t.tuitionId && (
                          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <h4 className="text-sm font-black text-slate-900">
                                Applied / Connected Tutors
                              </h4>
                              <Pill>Total: {appliedTutors.length}</Pill>
                            </div>

                            {appliedTutorsLoadingId === t.tuitionId ? (
                              <LoadingState message="Loading applied tutors..." />
                            ) : appliedTutors.length === 0 ? (
                              <EmptyState message="No tutors found for this tuition yet." />
                            ) : (
                              <div className="space-y-3">
                                {appliedTutors.map((tutor) => (
                                  <div
                                    key={tutor.tutorId}
                                    className="rounded-xl border border-slate-200 bg-white p-4"
                                  >
                                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                      <div>
                                        <div className="flex flex-wrap items-center gap-2">
                                          <h5 className="text-sm font-black text-slate-950">
                                            {tutor.tutorName || "Unnamed Tutor"}
                                          </h5>

                                          <Link
                                            to={`/tutor/${tutor.tutorId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <Pill tone="green">
                                              #{tutor.tutorId}
                                            </Pill>
                                          </Link>
                                        </div>

                                        <p className="mt-2 text-sm text-slate-600">
                                          Email:{" "}
                                          <span className="font-bold">
                                            {tutor.tutorEmail || "—"}
                                          </span>{" "}
                                          • Phone:{" "}
                                          <span className="font-bold">
                                            {tutor.tutorPhone || "—"}
                                          </span>{" "}
                                          • WhatsApp:{" "}
                                          <span className="font-bold">
                                            {tutor.tutorWhatsapp || "—"}
                                          </span>
                                          {tutor.tutorWhatsapp && (
                                            <a
                                              href={`https://web.whatsapp.com/send/?phone=${String(
                                                tutor.tutorWhatsapp,
                                              ).replace(
                                                /\D/g,
                                                "",
                                              )}&text&type=phone_number&app_absent=0`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="ml-2 inline-flex items-center rounded-lg bg-green-600 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-green-700"
                                            >
                                              WhatsApp
                                            </a>
                                          )}
                                        </p>

                                        <p className="mt-1 text-xs text-slate-400">
                                          Gender: {tutor.tutorGender || "—"} •
                                          Mode: {tutor.teachingMode || "—"} •
                                          Expected:{" "}
                                          {tutor.expectedRemuneration || "—"}
                                        </p>
                                      </div>

                                      <div className="text-left lg:text-right">
                                        <Pill>
                                          {tutor.methods || "connected"}
                                        </Pill>
                                        <p className="mt-2 text-xs font-semibold text-slate-400">
                                          Last connected:{" "}
                                          {formatDate(tutor.lastConnectedAt)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Tutor Management */}
          {tab === "tutors" && (
            <Card
              title="Tutor Management"
              description="Manage tutor quality, verification, credits, activity, commission due, and internal admin notes."
              right={
                <div className="flex flex-wrap items-center gap-2">
                  <Pill>Total: {adminTutorsTotal}</Pill>

                  <select
                    value={tutorStatus}
                    onChange={(e) => setTutorStatus(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All statuses</option>
                    {TUTOR_ADMIN_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>

                  <ActionButton onClick={fetchAdminTutors}>
                    Refresh
                  </ActionButton>
                </div>
              }
            >
              <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <DashboardMetricCard
                  label="Total Tutors"
                  value={adminTutorsSummary.totalTutors || 0}
                  hint="All registered tutor profiles."
                  tone="default"
                />

                <DashboardMetricCard
                  label="Active Tutors"
                  value={adminTutorsSummary.activeTutors || 0}
                  hint="Tutors currently allowed to apply."
                  tone="success"
                />

                <DashboardMetricCard
                  label="Suspended Tutors"
                  value={adminTutorsSummary.suspendedTutors || 0}
                  hint="Tutors restricted by admin."
                  tone="danger"
                />

                {user?.role === "admin" && (
                  <DashboardMetricCard
                    label="Commission Due"
                    value={`${Number(
                      adminTutorsSummary.totalCommissionDue || 0,
                    ).toLocaleString()} BDT`}
                    hint="Total pending commission from tutors."
                    tone="warning"
                  />
                )}
              </div>

              <div className="mb-5 flex flex-col gap-3 lg:flex-row">
                <input
                  value={tutorSearchInput}
                  onChange={(e) => setTutorSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTutorSearch();
                  }}
                  placeholder="Search by ID, name, phone, email, institute, department, subject, area..."
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400"
                />

                <div className="flex gap-2">
                  <ActionButton variant="dark" onClick={handleTutorSearch}>
                    Search
                  </ActionButton>

                  <ActionButton
                    onClick={() => {
                      setTutorSearchInput("");
                      setTutorSearch("");
                      setOffset(0);
                    }}
                  >
                    Clear
                  </ActionButton>
                </div>
              </div>

              {adminTutorsLoading ? (
                <LoadingState message="Loading tutors..." />
              ) : adminTutorsError ? (
                <ErrorState message={adminTutorsError} />
              ) : adminTutors.length === 0 ? (
                <EmptyState message="No tutors found." />
              ) : (
                <div className="space-y-4">
                  {adminTutors.map((tutor) => {
                    const draft = tutorDrafts[tutor.tutorId] || {};

                    const currentStatus =
                      draft.admin_status ?? tutor.adminStatus ?? "Active";

                    const currentNote =
                      draft.admin_note ?? tutor.adminNote ?? "";

                    return (
                      <div
                        key={tutor.tutorId}
                        className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                      >
                        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                          <div className="flex min-w-0 flex-1 gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-base font-black text-slate-950">
                                  {tutor.tutorName || "Unnamed Tutor"}
                                </h3>

                                <Link
                                  to={`/tutor/${tutor.tutorId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Pill tone="blue">
                                    Tutor ID: {tutor.tutorId}
                                  </Pill>
                                </Link>

                                <Pill tone={tutorStatusTone(currentStatus)}>
                                  {currentStatus}
                                </Pill>
                              </div>

                              <p className="mt-2 text-sm font-semibold text-slate-500">
                                {tutor.tutorEmail || "No email"}
                              </p>

                              <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-3">
                                <div>
                                  <span className="font-black text-slate-800">
                                    Phone:
                                  </span>{" "}
                                  {tutor.contactPhone || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    WhatsApp:
                                  </span>{" "}
                                  {tutor.contactWhatsapp || "—"}
                                  {tutor.contactWhatsapp && (
                                    <a
                                      href={`https://web.whatsapp.com/send/?phone=${String(
                                        tutor.contactWhatsapp,
                                      ).replace(
                                        /\D/g,
                                        "",
                                      )}&text&type=phone_number&app_absent=0`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-2 inline-flex items-center rounded-lg bg-green-600 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-green-700"
                                    >
                                      WhatsApp
                                    </a>
                                  )}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Gender:
                                  </span>{" "}
                                  {tutor.gender || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Institute:
                                  </span>{" "}
                                  {tutor.instituteName || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Department:
                                  </span>{" "}
                                  {tutor.fieldOfStudyName || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Degree:
                                  </span>{" "}
                                  {tutor.degreeName || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Mode:
                                  </span>{" "}
                                  {tutor.teachingMode || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Expected Salary:
                                  </span>{" "}
                                  {tutor.expectedRemuneration || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Credits:
                                  </span>{" "}
                                  {Number(tutor.applicationCredits || 0)}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Total Applied:
                                  </span>{" "}
                                  {Number(tutor.totalApplied || 0)}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Confirmed:
                                  </span>{" "}
                                  {Number(tutor.totalConfirmedTuitions || 0)}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Last Reviewed:
                                  </span>{" "}
                                  {formatDate(tutor.lastReviewedAt)}
                                </div>
                              </div>

                              <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-600 lg:grid-cols-2">
                                <div>
                                  <span className="font-black text-slate-800">
                                    Subjects / Classes:
                                  </span>{" "}
                                  {tutor.subjectsClasses || "—"}
                                </div>

                                <div>
                                  <span className="font-black text-slate-800">
                                    Preferred Areas:
                                  </span>{" "}
                                  {tutor.preferredAreas || "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <ActionButton
                              variant={
                                currentStatus === "Active" ? "danger" : "dark"
                              }
                              onClick={() =>
                                updateTutorDraft(tutor.tutorId, {
                                  admin_status:
                                    currentStatus === "Active"
                                      ? "Suspended"
                                      : "Active",
                                })
                              }
                            >
                              {currentStatus === "Active"
                                ? "Suspend"
                                : "Activate"}
                            </ActionButton>

                            <ActionButton
                              variant="dark"
                              disabled={tutorSavingId === tutor.tutorId}
                              onClick={() => saveTutor(tutor)}
                            >
                              {tutorSavingId === tutor.tutorId
                                ? "Saving..."
                                : "Save"}
                            </ActionButton>
                          </div>
                        </div>

                        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
                          <div>
                            <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                              Tutor Status
                            </label>

                            <select
                              value={currentStatus}
                              onChange={(e) =>
                                updateTutorDraft(tutor.tutorId, {
                                  admin_status: e.target.value,
                                })
                              }
                              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:border-slate-400"
                            >
                              {TUTOR_ADMIN_STATUSES.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </select>
                          </div>

                          {user?.role === "admin" && (
                            <div>
                              <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                                Commission Due
                              </label>

                              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-black text-amber-700">
                                {Number(
                                  tutor.commissionDue || 0,
                                ).toLocaleString()}{" "}
                                BDT
                              </div>
                            </div>
                          )}
                          <div>
                            <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                              Quick Contact
                            </label>

                            <div className="mt-2 flex flex-wrap gap-2">
                              {tutor.contactPhone ? (
                                <a
                                  href={`tel:${tutor.contactPhone}`}
                                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
                                >
                                  Call
                                </a>
                              ) : null}

                              {tutor.contactWhatsapp ? (
                                <a
                                  href={`https://wa.me/${String(tutor.contactWhatsapp).replace(/\D/g, "")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-black text-emerald-700 hover:bg-emerald-100"
                                >
                                  WhatsApp
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="text-xs font-black uppercase tracking-wide text-slate-400">
                            Admin Note
                          </label>

                          <textarea
                            value={currentNote}
                            onChange={(e) =>
                              updateTutorDraft(tutor.tutorId, {
                                admin_note: e.target.value,
                              })
                            }
                            placeholder="Internal note for this tutor..."
                            rows={3}
                            className="mt-2 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Application Management */}
          {tab === "applications" && (
            <ApplicationManagement
              apiBase={API_BASE}
              authHeaders={authHeaders}
              onUnauthorized={handleLogout}
              userRole={user?.role}
            />
          )}

          {/* Data Management */}
          {tab === "dataManagement" && (
            <DataManagement
              apiBase={API_BASE}
              authHeaders={authHeaders}
              onUnauthorized={handleLogout}
            />
          )}

          {/* Confirmation & Commission Tracker */}
          {tab === "payTrack" && (
            <PayTrack
              apiBase={API_BASE}
              authHeaders={authHeaders}
              onUnauthorized={handleLogout}
            />
          )}

          {/* Topups */}
          {tab === "topups" && (
            <Card
              title="Manual Topups"
              description="Review manual tutor credit topup requests."
              right={
                <div className="flex flex-wrap items-center gap-2">
                  <Pill>Total: {topupsTotal}</Pill>

                  <select
                    value={topupStatus}
                    onChange={(e) => setTopupStatus(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:border-slate-400"
                  >
                    <option value="pending">pending</option>
                    <option value="approved">approved</option>
                    <option value="rejected">rejected</option>
                  </select>

                  <ActionButton onClick={fetchTopups}>Refresh</ActionButton>
                </div>
              }
            >
              {topupsLoading ? (
                <LoadingState message="Loading topups..." />
              ) : topupsError ? (
                <ErrorState message={topupsError} />
              ) : topups.length === 0 ? (
                <EmptyState message="No topup requests found." />
              ) : (
                <div className="overflow-hidden rounded-xl border border-slate-100">
                  <div className="divide-y divide-slate-100">
                    {topups.map((r) => (
                      <ListRow key={r.id}>
                        <div className="flex flex-col gap-4 px-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-sm font-black text-slate-900">
                                Request #{r.id}
                              </h3>
                              <Pill
                                tone={
                                  r.status === "approved"
                                    ? "success"
                                    : r.status === "rejected"
                                      ? "danger"
                                      : "warning"
                                }
                              >
                                {r.status}
                              </Pill>
                            </div>

                            <p className="mt-2 text-sm text-slate-600">
                              Tutor:{" "}
                              <span className="font-bold">
                                {r.tutor_id || "—"}
                              </span>{" "}
                              • Pack:{" "}
                              <span className="font-bold">
                                {r.pack_id || "—"}
                              </span>{" "}
                              • Amount:{" "}
                              <span className="font-bold">
                                {r.amount_bdt || 0} BDT
                              </span>{" "}
                              • Credits:{" "}
                              <span className="font-bold">
                                {r.credits || 0}
                              </span>
                            </p>

                            <p className="mt-1 text-xs text-slate-400">
                              Sender: {r.sender_number || "—"} • Created:{" "}
                              {formatDate(r.created_at)}
                            </p>
                          </div>

                          {r.status === "pending" ? (
                            <div className="flex flex-wrap gap-2">
                              <ActionButton
                                variant="dark"
                                onClick={() => approve(r.id)}
                              >
                                Approve
                              </ActionButton>
                              <ActionButton
                                variant="danger"
                                onClick={() => openReject(r.id)}
                              >
                                Reject
                              </ActionButton>
                            </div>
                          ) : (
                            <Pill tone="success">Done</Pill>
                          )}
                        </div>
                      </ListRow>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Tutor Profile Reveals */}
          {tab === "tutorReveals" && (
            <Card
              title="Track Students"
              description="Track students who revealed tutor contact information from tutor profiles."
              right={
                <div className="flex flex-wrap items-center gap-2">
                  <Pill>Total: {tutorRevealsTotal}</Pill>
                  <ActionButton onClick={fetchTutorReveals}>
                    Refresh
                  </ActionButton>
                </div>
              }
            >
              {tutorLoading ? (
                <LoadingState message="Loading tutor profile reveals..." />
              ) : tutorError ? (
                <ErrorState message={tutorError} />
              ) : tutorReveals.length === 0 ? (
                <EmptyState message="No tutor profile reveals found." />
              ) : (
                <div className="overflow-hidden rounded-xl border border-slate-100">
                  <div className="divide-y divide-slate-100">
                    {tutorReveals.map((r) => (
                      <ListRow key={r.id}>
                        <div className="px-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm font-black text-slate-900">
                              Reveal #{r.id}
                            </h3>
                            <Pill tone="blue">{r.method}</Pill>
                          </div>

                          <p className="mt-2 text-sm text-slate-600">
                            Tutor:{" "}
                            <span className="font-bold">
                              {r.tutorId || "—"}
                            </span>{" "}
                            • Student:{" "}
                            <span className="font-bold">
                              {r.studentId || "—"}
                            </span>
                          </p>

                          <p className="mt-1 text-xs text-slate-400">
                            Revealed: {formatDate(r.revealedAt)} • IP:{" "}
                            {r.ip || "—"}
                          </p>
                        </div>
                      </ListRow>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Pagination */}
          {/* Pagination */}
          {![
            "dashboard",
            "applications",
            "payTrack",
            "dataManagement",
          ].includes(tab) && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <ActionButton
                disabled={!canPrev}
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
              >
                Prev
              </ActionButton>

              <div className="text-sm font-semibold text-slate-500">
                Showing{" "}
                <span className="font-black text-slate-900">
                  {totalForTab === 0 ? 0 : offset + 1}
                </span>
                –
                <span className="font-black text-slate-900">
                  {Math.min(offset + limit, totalForTab)}
                </span>{" "}
                of{" "}
                <span className="font-black text-slate-900">{totalForTab}</span>
              </div>

              <ActionButton
                disabled={!canNext}
                onClick={() => setOffset((o) => o + limit)}
              >
                Next
              </ActionButton>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {rejectOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4"
          onClick={() => setRejectOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-slate-950">
                  Reject topup
                </h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Request #{rejectId}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setRejectOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-500 hover:bg-slate-50"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="mt-5">
              <label className="text-sm font-black text-slate-800">
                Reason{" "}
                <span className="font-semibold text-slate-400">(optional)</span>
              </label>

              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. payment not found"
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400"
              />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ActionButton onClick={() => setRejectOpen(false)}>
                Cancel
              </ActionButton>

              <ActionButton variant="danger" onClick={reject}>
                Reject
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
